import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@workspace/ui/components/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthCard, authStyles, authSubmitProps } from '../components/auth-card';
import type { LoginCredentials } from '../types';

const loginSchema = z.object({
    email: z.string().min(1, 'L\'email ou l\'identifiant est requis'),
    password: z.string().min(1, 'Le mot de passe est requis'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export interface LoginPageProps {
    /** Titre principal (défaut: "Connexion") */
    title?: string;
    /** Sous-titre sous le titre */
    description?: string;
    /** Nom de l'app affiché à côté du logo (lockup) */
    appName?: string;
    /** Hook useLogin configuré pour l'app */
    useLoginHook: () => {
        mutateAsync: (credentials: LoginCredentials) => Promise<unknown>;
        isPending: boolean;
    };
}

/**
 * Page de login SSO mutualisée (cms, deals, calendar). Design aligné sur la vue
 * Blade `sso::login` du kit. Gère le throttling PASS (HTTP 429).
 */
export function LoginPage({
    title = 'Connexion',
    description = 'Accès réservé aux conseillers APRIL Moto',
    appName,
    useLoginHook,
}: LoginPageProps) {
    const loginMutation = useLoginHook();
    const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

    const form = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: '', password: '' },
    });

    useEffect(() => {
        if (rateLimitSeconds <= 0) {
            return;
        }
        const timer = setTimeout(() => setRateLimitSeconds((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [rateLimitSeconds]);

    const handleSubmit = async (data: LoginFormData) => {
        try {
            await loginMutation.mutateAsync(data);
        } catch (error) {
            const response = (error as { response?: { status?: number; headers?: Record<string, string> } }).response;
            if (response?.status === 429) {
                const retryAfter = Number.parseInt(response.headers?.['retry-after'] ?? '60', 10);
                setRateLimitSeconds(Number.isNaN(retryAfter) ? 60 : retryAfter);
            }
        }
    };

    return (
        <AuthCard appName={appName} heading={title} subtitle={description}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Email ou identifiant</FormLabel>
                                <FormControl>
                                    <Input {...field} type="text" autoComplete="username" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Mot de passe</FormLabel>
                                <FormControl>
                                    <Input type="password" autoComplete="current-password" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {rateLimitSeconds > 0 && (
                        <p style={authStyles.rate}>Trop de tentatives. Réessayez dans {rateLimitSeconds}s.</p>
                    )}

                    <Button type="submit" {...authSubmitProps} loading={loginMutation.isPending} disabled={rateLimitSeconds > 0}>
                        Se connecter
                    </Button>
                </form>
            </Form>
        </AuthCard>
    );
}
